import { message } from 'antd';
import { AxiosInstance } from 'axios';
import { Dayjs } from 'dayjs';

import FileData from '@/model/General/FileData';
import User from '@/model/General/User';
import { PackageMessage } from '@/model/Logistic/PackageMessage';
import { PACKAGE_STATES } from '@/model/Logistic/PackageStates';
import { Reception } from '@/model/Logistic/Reception/Reception';
import { ShippingLabel } from '@/model/Logistic/ShippingLabel/ShippingLabel';

import ReceptionApi from './ReceptionApi';

class ReceptionRepository {
  receptionApi: ReceptionApi;

  constructor(axiosInstance: AxiosInstance) {
    this.receptionApi = new ReceptionApi(axiosInstance);
  }

  saveNewAddress = async shipmentData => {
    const shippingLabel: ShippingLabel = {
      address: shipmentData.receiver_address,
      email: shipmentData.receiver_email,
      phone_number: shipmentData.receiver_phone,
      localidad: shipmentData.receiver_localidad,
      postal_code: shipmentData.receiver_postal_code,
      company: shipmentData.company,
      contact: shipmentData.receiver_name,

      description: `${shipmentData.receiver_address} - ${shipmentData.receiver_name} - ${shipmentData.receiver_phone} - ${shipmentData.receiver_email}`,
    };

    try {
      const response = await this.receptionApi.createAddress(shippingLabel);

      return response.data;
    } catch (error) {
      console.error('Error saving address:', error);
      throw error;
    }
  };

  printShippingLabel(receptionId: number) {
    return this.receptionApi
      .printShippingLabel(receptionId)
      .then(() => message.success('Etiqueta de envío generada correctamente'))
      .catch(() => message.error('Error al generar la etiqueta de envío'));
  }

  saveFiles = async (files: FileData[], packageId: number) => {
    try {
      const filesToUpload = files.filter(file => !file.removed && file.blobFile);
      const filesToRemove = files.filter(file => file.id && file.removed);
      const promises: Promise<any>[] = [];

      if (filesToUpload.length > 0) {
        promises.push(this.receptionApi.uploadFiles(filesToUpload, packageId));
      }

      if (filesToRemove.length > 0) {
        promises.push(this.receptionApi.removeFiles(filesToRemove));
      }

      await Promise.all(promises);
    } catch (error) {
      console.error('Error uploading files:', error);
      throw error;
    }
  };

  updateReceptionState = async (
    receptionsId: number | number[],
    newState: PACKAGE_STATES,
    date?: Dayjs,
  ) => {
    try {
      if (Array.isArray(receptionsId)) {
        const response = await Promise.all(
          receptionsId.map(receptionId =>
            this.receptionApi.updateReceptionState(receptionId, newState, date),
          ),
        );

        message.success('Estado de las recepciones actualizado correctamente');

        return response.map(res => res.data);
      } else {
        const response = await this.receptionApi.updateReceptionState(receptionsId, newState, date);

        message.success('Estado de la recepción actualizado correctamente');

        return response.data;
      }
    } catch (error) {
      console.error('Error updating reception state:', error);
      message.error('Error al actualizar el estado de la recepción');
      throw error;
    }
  };

  updateReceptionShipper = async (receptionId: number, shipperId: number) => {
    try {
      const response = await this.receptionApi.updateReceptionShipper(receptionId, shipperId);

      message.success('Operador logístico actualizado correctamente');

      return response.data;
    } catch (error) {
      message.error('Error al actualizar el operador logístico');
      throw error;
    }
  };
  updateReceptionTechnician = async (receptionId: number, technicianId?: number) => {
    try {
      const response = await this.receptionApi.updateReceptionTechnician(receptionId, technicianId);

      message.success('Técnico actualizado correctamente');

      return response.data;
    } catch (error) {
      message.error('Error al actualizar el técnico');
      throw error;
    }
  };
  updateReception = async (receptionId: number, receptionData: Partial<Reception>) => {
    try {
      const response = await this.receptionApi.updateReception(receptionId, receptionData);

      message.success('Recepción actualizada correctamente');

      return response;
    } catch (error) {
      message.error('Error al actualizar la recepción');
      throw error;
    }
  };

  getMessages = async (receptionId: number) => {
    try {
      const messages = await this.receptionApi.getMessages(receptionId);
      const messagesData: PackageMessage[] = messages.map(msg => ({
        ...msg,
        origin_user_data:
          typeof msg.origin_user_data === 'number'
            ? msg.origin_user_data
            : new User(msg.origin_user_data),
      }));

      return messagesData;
    } catch (error) {
      message.error('Error al obtener los mensajes de la recepción');
      throw error;
    }
  };

  postMessage = async (receptionId: number, packageMessage: PackageMessage) => {
    try {
      const response = await this.receptionApi.postMessage(receptionId, packageMessage);

      return response.data;
    } catch (error) {
      message.error('Error al enviar el mensaje');
      throw error;
    }
  };

  sendClaimEmail = async (receptionId: number, emails: string) => {
    try {
      const response = await this.receptionApi.sendClaimEmail(receptionId, emails);

      return response;
    } catch (error) {
      message.error('Error al enviar el mensaje');
      throw error;
    }
  };
}

export default ReceptionRepository;
